import { DEFAULT_COLOR_LABEL } from '../../utils/productColorLabels';

/**
 * Same preset colors as Add New Product — keep Manage Stock aligned.
 */
export const PRESET_PRODUCT_COLORS = [
  { name: DEFAULT_COLOR_LABEL, code: '#9E9E9E' },
  { name: 'Red', code: '#FF0000' },
  { name: 'Blue', code: '#0000FF' },
  { name: 'Green', code: '#00FF00' },
  { name: 'Black', code: '#000000' },
  { name: 'White', code: '#FFFFFF' },
  { name: 'Yellow', code: '#FFFF00' },
  { name: 'Pink', code: '#FFC0CB' },
  { name: 'Purple', code: '#800080' },
  { name: 'Orange', code: '#FFA500' },
  { name: 'Gray', code: '#808080' },
  { name: 'Brown', code: '#A52A2A' },
  { name: 'Navy', code: '#000080' },
];

/** Same size checklist as Add New Product (no XXXL unless added elsewhere). */
export const PRESET_PRODUCT_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];

/** Kids clothing — toddler / youth style labels (stored as-is on product + stock keys). */
export const KIDS_CLOTHING_SIZES = ['2T', '3T', '4T', '5T', '6', '8', '10', '12'];

/**
 * Shoe sizes: EU with US equivalent (single string for storage, cart, PDP).
 * Mapping: US 5–12 ↔ EU 36–43 (common retail convention).
 */
export const SHOE_SIZE_PRESET_STRINGS = [
  'EU 36 (US 5)',
  'EU 37 (US 6)',
  'EU 38 (US 7)',
  'EU 39 (US 8)',
  'EU 40 (US 9)',
  'EU 41 (US 10)',
  'EU 42 (US 11)',
  'EU 43 (US 12)',
];

/**
 * Returns size chip options for Add Product / stock UI from main category display name.
 * Matching is case-insensitive on the category name string (e.g. from admin "Main Category: Kids Clothing").
 */
export function getSizePresetsForMainCategoryName(mainCategoryName) {
  const raw = String(mainCategoryName || '').trim().toLowerCase();
  if (!raw) return PRESET_PRODUCT_SIZES;

  const looksKids =
    raw.includes('kid') &&
    (raw.includes('cloth') || raw.includes('wear') || raw.includes('apparel') || raw.includes('toddler'));
  if (looksKids) return KIDS_CLOTHING_SIZES;

  if (raw.includes('shoe') || raw === 'footwear' || raw.includes('footwear')) {
    return SHOE_SIZE_PRESET_STRINGS;
  }

  return PRESET_PRODUCT_SIZES;
}

/**
 * Extra named colors for type-to-search (merged with presets + custom hex; deduped by name).
 */
export const VARIANT_COLOR_SEARCH_EXTRAS = [
  { name: 'Maroon', code: '#800000' },
  { name: 'Grey', code: '#808080' },
  { name: 'Beige', code: '#F5F5DC' },
  { name: 'Khaki', code: '#C3B091' },
  { name: 'Teal', code: '#008080' },
  { name: 'Coral', code: '#FF7F50' },
  { name: 'Turquoise', code: '#40E0D0' },
  { name: 'Gold', code: '#FFD700' },
  { name: 'Silver', code: '#C0C0C0' },
  { name: 'Bronze', code: '#CD7F32' },
  { name: 'Ivory', code: '#FFFFF0' },
  { name: 'Cream', code: '#FFFDD0' },
  { name: 'Peach', code: '#FFDAB9' },
  { name: 'Lavender', code: '#E6E6FA' },
  { name: 'Lilac', code: '#C8A2C8' },
  { name: 'Magenta', code: '#FF00FF' },
  { name: 'Cyan', code: '#00FFFF' },
  { name: 'Mint', code: '#98FB98' },
  { name: 'Olive', code: '#808000' },
  { name: 'Forest Green', code: '#228B22' },
  { name: 'Emerald', code: '#50C878' },
  { name: 'Sky Blue', code: '#87CEEB' },
  { name: 'Royal Blue', code: '#4169E1' },
  { name: 'Indigo', code: '#4B0082' },
  { name: 'Violet', code: '#EE82EE' },
  { name: 'Rose', code: '#FF66CC' },
  { name: 'Salmon', code: '#FA8072' },
  { name: 'Tan', code: '#D2B48C' },
  { name: 'Charcoal', code: '#36454F' },
  { name: 'Slate', code: '#708090' },
  { name: 'Wine', code: '#722F37' },
  { name: 'Burgundy', code: '#800020' },
  { name: 'Rust', code: '#B7410E' },
  { name: 'Mustard', code: '#FFDB58' },
  { name: 'Lemon', code: '#FFF44F' },
  { name: 'Aqua', code: '#00FFFF' },
  { name: 'Copper', code: '#B87333' },
  { name: 'Plum', code: '#8E4585' },
  { name: 'Crimson', code: '#DC143C' },
  { name: 'Scarlet', code: '#FF2400' },
  { name: 'Cerulean', code: '#2A52BE' },
  { name: 'Periwinkle', code: '#CCCCFF' },
  { name: 'Taupe', code: '#483C32' },
  { name: 'Sand', code: '#C2B280' },
  { name: 'Blush', code: '#DE5D83' },
  { name: 'Fuchsia', code: '#FF00FF' },
  { name: 'Chartreuse', code: '#7FFF00' },
  { name: 'Sage', code: '#9DC183' },
  { name: 'Denim', code: '#1560BD' },
  { name: 'Midnight Blue', code: '#191970' },
  /* —— Extended search palette (fashion / unstitched / festive) —— */
  { name: 'Off White', code: '#FAF9F6' },
  { name: 'Sea Green', code: '#2E8B57' },
  { name: 'Pistachio', code: '#93C572' },
  { name: 'Dusty Rose', code: '#DCAE96' },
  { name: 'Dusty Pink', code: '#D8A7A7' },
  { name: 'Pastel Pink', code: '#FFD1DC' },
  { name: 'Pastel Blue', code: '#AEC6CF' },
  { name: 'Pastel Green', code: '#BFD8B8' },
  { name: 'Mauve', code: '#E0B0FF' },
  { name: 'Mulberry', code: '#C54B8C' },
  { name: 'Sapphire', code: '#0F52BA' },
  { name: 'Pearl', code: '#EAE0C8' },
  { name: 'Steel Blue', code: '#4682B4' },
  { name: 'Powder Blue', code: '#B0E0E6' },
  { name: 'Baby Pink', code: '#F4C2C2' },
  { name: 'Baby Blue', code: '#89CFF0' },
  { name: 'Hot Pink', code: '#FF69B4' },
  { name: 'Deep Red', code: '#8B0000' },
  { name: 'Dark Green', code: '#013220' },
  { name: 'Light Grey', code: '#D3D3D3' },
  { name: 'Dark Grey', code: '#A9A9A9' },
  { name: 'Camel', code: '#C19A6B' },
  { name: 'Chocolate', code: '#7B3F00' },
  { name: 'Coffee', code: '#6F4E37' },
  { name: 'Nude', code: '#E3BC9A' },
  { name: 'Terracotta', code: '#E2725B' },
  { name: 'Brick', code: '#CB4154' },
  { name: 'Marigold', code: '#FCAE1E' },
  { name: 'Jade', code: '#00A86B' },
  { name: 'Honey', code: '#FFC30B' },
  { name: 'Champagne', code: '#F7E7CE' },
  { name: 'Rose Gold', code: '#B76E79' },
  { name: 'Rosewood', code: '#65000B' },
  { name: 'Mahogany', code: '#C04000' },
  { name: 'Ebony', code: '#555D50' },
  { name: 'Ash', code: '#B2BEB5' },
  { name: 'Oatmeal', code: '#D4C4B0' },
  { name: 'Linen', code: '#FAF0E6' },
  { name: 'Ecru', code: '#C8B8A0' },
  { name: 'Stone', code: '#928E85' },
  { name: 'Parchment', code: '#F1E9D2' },
  { name: 'Vanilla', code: '#F3E5AB' },
  { name: 'Butter', code: '#FFF1B5' },
  { name: 'Apricot', code: '#FBCEB1' },
  { name: 'Tangerine', code: '#F28500' },
  { name: 'Melon', code: '#FEBAAD' },
  { name: 'Raspberry', code: '#E30B5C' },
  { name: 'Grape', code: '#6F2DA8' },
  { name: 'Orchid', code: '#DA70D6' },
  { name: 'Cobalt', code: '#0047AB' },
  { name: 'Azure', code: '#007FFF' },
  { name: 'Kelly Green', code: '#4CBB17' },
  { name: 'Hunter Green', code: '#355E3B' },
  { name: 'Moss', code: '#8A9A5B' },
  { name: 'Pine', code: '#01796F' },
  { name: 'Wisteria', code: '#C9A0DC' },
  { name: 'Amethyst', code: '#9966CC' },
  { name: 'Aubergine', code: '#614051' },
  { name: 'Eggplant', code: '#614051' },
  { name: 'Raisin', code: '#242124' },
  { name: 'Cinnamon', code: '#D2691E' },
  { name: 'Ginger', code: '#B06500' },
  { name: 'Caramel', code: '#AF6E4D' },
  { name: 'Toffee', code: '#755C48' },
  { name: 'Biscuit', code: '#E4C7B8' },
  { name: 'Snow', code: '#FFFAFA' },
  { name: 'Frost', code: '#E8F4F8' },
  { name: 'Ice Blue', code: '#99D6EA' },
  { name: 'Arctic Blue', code: '#6CA6CD' },
  { name: 'Gunmetal', code: '#2C3539' },
  { name: 'Pewter', code: '#899499' },
  { name: 'Rani Pink', code: '#E30B5D' },
  { name: 'Mehndi', code: '#5D4E37' },
  { name: 'Antique Gold', code: '#CFB53B' },
  { name: 'Zari Gold', code: '#D4AF37' },
  { name: 'Ferozi', code: '#00CED1' },
  { name: 'Pista', code: '#93C572' },
  { name: 'Skin', code: '#E8C4A0' },
  { name: 'Dhani', code: '#D4AF37' },
  { name: 'Parrot Green', code: '#50C878' },
  { name: 'Onion Pink', code: '#D8A7C8' },
  { name: 'Tea Pink', code: '#E8C8D0' },
  { name: 'Magenta Pink', code: '#FF1493' },
  { name: 'Steel Grey', code: '#71797E' },
  { name: 'Graphite', code: '#41424C' },
  { name: 'Fog', code: '#CCCCCC' },
  { name: 'Heather', code: '#9E7B7B' },
  { name: 'Natural', code: '#D4C4A8' },
  { name: 'Walnut', code: '#5D432C' },
  { name: 'Sepia', code: '#704214' },
  { name: 'Copper Rose', code: '#996666' },
  { name: 'Dull Gold', code: '#B8860B' },
  { name: 'Antique Rose', code: '#C08081' },
  { name: 'Ice Pink', code: '#F8D7E5' },
  { name: 'Lemonade', code: '#F0E68C' },
  { name: 'Mint Cream', code: '#F5FFFA' },
  { name: 'Spring Green', code: '#00FF7F' },
  { name: 'Seaweed', code: '#354E52' },
  { name: 'Iris', code: '#5A4FCF' },
  { name: 'Bondi Blue', code: '#0095B6' },
  { name: 'Robin Egg Blue', code: '#00CCCC' },
  { name: 'Denim Blue', code: '#2243B6' },
  { name: 'Steel', code: '#71797E' },
  { name: 'Zinc', code: '#7A8694' },
  { name: 'Almond', code: '#EFDECD' },
  { name: 'Cashew', code: '#D2B48C' },
  { name: 'Papaya', code: '#FFEFD5' },
  { name: 'Guava', code: '#F68B8B' },
  { name: 'Lime', code: '#32CD32' },
  { name: 'Neon Pink', code: '#FF6EC7' },
  { name: 'Neon Green', code: '#39FF14' },
  { name: 'Electric Blue', code: '#7DF9FF' },
  { name: 'Royal Purple', code: '#7851A9' },
  { name: 'Deep Purple', code: '#301934' },
  { name: 'Ocean Blue', code: '#4F97A3' },
  { name: 'Slate Blue', code: '#6A5ACD' },
  { name: 'Cornflower', code: '#6495ED' },
  { name: 'Tomato', code: '#FF6347' },
  { name: 'Pumpkin', code: '#FF7518' },
  { name: 'Mango', code: '#FFBE0B' },
  { name: 'Lilac Grey', code: '#9896A4' },
  { name: 'Blue Grey', code: '#6699CC' },
  { name: 'Green Grey', code: '#7D8471' },
  { name: 'Warm White', code: '#F5F2E9' },
  { name: 'Cool Grey', code: '#8C92AC' },
];

/**
 * Merge preset + extras + custom palette entries; first occurrence wins per lowercase name.
 */
export function mergeSearchableColorOptions(presetColors, extras, customPalette) {
  const seen = new Set();
  const out = [];
  const add = (arr) => {
    if (!Array.isArray(arr)) return;
    for (const c of arr) {
      if (!c || !c.name) continue;
      const k = String(c.name).trim().toLowerCase();
      if (!k || seen.has(k)) continue;
      seen.add(k);
      out.push({ name: c.name.trim(), code: c.code || '#ccc' });
    }
  };
  add(presetColors);
  add(extras);
  add(customPalette);
  return out;
}

/** Filter / rank colors as user types; prioritise names that start with the query. */
export function filterColorsByQuery(colorOptions, query) {
  const s = (query || '').trim().toLowerCase();
  if (!colorOptions || colorOptions.length === 0) return [];
  if (!s) return colorOptions.slice(0, 24);
  const filtered = colorOptions.filter((c) => String(c.name).toLowerCase().includes(s));
  filtered.sort((a, b) => {
    const an = String(a.name).toLowerCase();
    const bn = String(b.name).toLowerCase();
    const aw = an.startsWith(s) ? 0 : 1;
    const bw = bn.startsWith(s) ? 0 : 1;
    if (aw !== bw) return aw - bw;
    return an.localeCompare(bn);
  });
  return filtered.slice(0, 32);
}
